import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FirmaBankverbindungen from './FirmaBankverbindungen';
import FirmaBriefpapier from './FirmaBriefpapier';
import FirmaBelegtexte from './FirmaBelegtexte';
import FirmaMailvorlagen from './FirmaMailvorlagen';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

/**
 * Firmenstammdaten des eigenen Mandanten (Belegvorlagen-Spec 31.08.2026).
 *
 * Löst die früher im PDF-Generator hart kodierte FIRMA-Konstante ab (siehe
 * `firma_service.py`). `adresse` ist ein älteres Freitextfeld ohne eigenes
 * Formularfeld in dieser Maske — PUT sendet es unverändert zurück; ein
 * Editor dafür ist bewusst nicht Teil dieser Sektion, die strukturierten
 * E-Rechnungs-Pendants `strasse`/`plz`/`ort` decken den Bedarf ab.
 */
export interface Firma {
  firmenname: string; adresse: string | null;
  strasse: string | null; plz: string | null; ort: string | null;
  land_code: string | null;
  telefon: string | null; website: string | null; email: string | null;
  email_rechnung: string | null;
  kontakt_rechnung_name: string | null; kontakt_rechnung_telefon: string | null;
  ust_id: string | null; steuernummer: string | null;
  primaerfarbe: string | null;
  handelsregister: string | null; registergericht: string | null;
  geschaeftsfuehrer: string | null; sitz: string | null; gerichtsstand: string | null;
  eigentumsvorbehalt: string | null; kleinunternehmer: boolean;
  logo_pfad: string | null; briefpapier_kopf: string | null; briefpapier_fuss: string | null;
  /** E-Rechnungs-Pflicht (Paket 3): wirksamer Stichtag (ISO), der gespeicherte Wert (null = Standard)
      und der Standard selbst. */
  erechnung_pflicht_ab?: string;
  erechnung_pflicht_ab_gesetzt?: string | null;
  erechnung_pflicht_ab_standard?: string;
}

/** Stichtag, ab dem Rechnungen an inländische Unternehmer E-Rechnungen sein müssen, wenn der
    Vorjahresumsatz über 800.000 € lag (sonst gilt der Standard 01.01.2028). */
const PFLICHT_AB_GROSSBETRIEB = '2027-01-01';
const PFLICHT_AB_MIN = '2025-01-01';
const PFLICHT_AB_MAX = '2030-12-31';

const datumDe = (iso?: string | null) => {
  const t = (iso ?? '').slice(0, 10).split('-');
  return t.length === 3 ? `${t[2]}.${t[1]}.${t[0]}` : '';
};

type Reiter = 'stammdaten' | 'banken' | 'briefpapier' | 'texte' | 'mails';

const REITER: Array<{ id: Reiter; label: string }> = [
  { id: 'stammdaten', label: 'Stammdaten' },
  { id: 'banken', label: 'Bankverbindungen' },
  { id: 'briefpapier', label: 'Briefpapier' },
  { id: 'texte', label: 'Belegtexte' },
  { id: 'mails', label: 'E-Mail-Texte' },
];

const TEXTFELDER: Array<[keyof Firma, string]> = [
  ['firmenname', 'Firmenname'], ['strasse', 'Straße'], ['plz', 'PLZ'], ['ort', 'Ort'],
  ['land_code', 'Land (ISO-Code, z.B. DE)'],
  ['telefon', 'Telefon'], ['website', 'Website'], ['email', 'E-Mail'],
  ['email_rechnung', 'E-Mail für Rechnungen'],
  ['kontakt_rechnung_name', 'Ansprechpartner Rechnung (Pflicht für XRechnung)'],
  ['kontakt_rechnung_telefon', 'Telefon Ansprechpartner Rechnung (sonst Firmen-Telefon)'],
  ['ust_id', 'USt-IdNr.'], ['steuernummer', 'Steuernummer'],
  ['handelsregister', 'Handelsregister (HRB)'], ['registergericht', 'Registergericht'],
  ['geschaeftsfuehrer', 'Geschäftsführer'], ['sitz', 'Sitz der Gesellschaft'],
  ['gerichtsstand', 'Gerichtsstand'],
];

/**
 * Einstellungen -> Firma: Stammdaten, Bankverbindungen, Briefpapier und
 * Belegtexte des eigenen Mandanten, alles über `/api/firma/*` (Task 5/6).
 * Diese Komponente hält nur die Stammdaten selbst; die anderen drei Reiter
 * laden ihre Daten eigenständig und melden Fehler über `onFehler` an das
 * gemeinsame Banner hier oben.
 */
const FirmaSection: React.FC = () => {
  const [firma, setFirma] = useState<Firma | null>(null);
  const [reiter, setReiter] = useState<Reiter>('stammdaten');
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFehler(null);
    axios.get<Firma>(`${API_URL}/api/firma`)
      .then(r => setFirma(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Firmendaten konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reiterwechsel räumt Meldungen des vorherigen Reiters ab — sonst hängt
  // z.B. ein Bankverbindungen-Fehler noch am Banner, während der Nutzer
  // längst bei den Belegtexten ist.
  const reiterWechseln = (r: Reiter) => {
    setReiter(r);
    setFehler(null);
    setGespeichert(false);
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firma) return;
    setFehler(null);
    setSaving(true);
    try {
      // Das Eingabefeld hält den gespeicherten Wert (leer = Standard); der Server erwartet ihn als
      // erechnung_pflicht_ab (ISO-Datum oder null).
      const r = await axios.put<Firma>(`${API_URL}/api/firma`, {
        ...firma,
        erechnung_pflicht_ab: firma.erechnung_pflicht_ab_gesetzt || null,
      });
      setFirma(r.data);
      setGespeichert(true);
      window.setTimeout(() => setGespeichert(false), 2000);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      setFehler(`Speichern fehlgeschlagen: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Lade Firmendaten…</div>;
  if (!firma) {
    return (
      <div>
        <h3 style={{ marginTop: 0 }}>Firma</h3>
        <div style={{ color: 'var(--red)' }}>{fehler ?? 'Keine Firmendaten vorhanden.'}</div>
      </div>
    );
  }

  const feld = (schluessel: keyof Firma, label: string) => (
    <label key={schluessel} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label}
      <input
        type="text"
        required={schluessel === 'firmenname'}
        value={(firma[schluessel] as string | null) ?? ''}
        onChange={e => setFirma({ ...firma, [schluessel]: e.target.value })}
        style={{ padding: '7px 10px' }}
      />
    </label>
  );

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Firma</h3>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Stammdaten, Bankverbindungen, Briefpapier, Standardtexte für Belege und E-Mail-Texte
        für den Versand. Diese Daten füllen den Briefkopf und die E-Rechnung
        (ZUGFeRD/XRechnung).
      </p>

      <nav style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--bone-3)' }}>
        {REITER.map(r => (
          <button
            key={r.id}
            type="button"
            onClick={() => reiterWechseln(r.id)}
            style={{
              padding: '10px 16px',
              marginBottom: -1,
              border: 'none',
              borderBottom: `2px solid ${reiter === r.id ? 'var(--green-900)' : 'transparent'}`,
              background: 'transparent',
              color: reiter === r.id ? 'var(--green-900)' : 'var(--ink-2)',
              fontWeight: reiter === r.id ? 600 : 500,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {r.label}
          </button>
        ))}
      </nav>

      {fehler && (
        <div role="alert" style={{ padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--red)', background: 'var(--bone-2)', border: '1px solid var(--bone-3)' }}>
          {fehler}
        </div>
      )}
      {gespeichert && (
        <div style={{ padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--green-500)', background: 'var(--bone-2)', border: '1px solid var(--bone-3)' }}>
          ✓ Gespeichert.
        </div>
      )}

      {reiter === 'stammdaten' && (
        <form onSubmit={speichern}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 16 }}>
            {TEXTFELDER.map(([k, label]) => feld(k, label))}
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16, maxWidth: 320 }}>
            Primärfarbe
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                value={firma.primaerfarbe ?? ''}
                onChange={e => setFirma({ ...firma, primaerfarbe: e.target.value })}
                placeholder="#1a73e8"
                style={{ padding: '7px 10px', flex: 1 }}
              />
              <span
                aria-hidden="true"
                title="Vorschau"
                style={{
                  width: 28, height: 28, flex: 'none', borderRadius: 4,
                  border: '1px solid var(--bone-3)',
                  background: firma.primaerfarbe || 'transparent',
                }}
              />
            </span>
            <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              Wird derzeit nur gespeichert und noch nicht in der Oberfläche angewendet.
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 16px', fontSize: 14, color: 'var(--ink)' }}>
            <input
              type="checkbox"
              checked={firma.kleinunternehmer}
              onChange={e => setFirma({ ...firma, kleinunternehmer: e.target.checked })}
            />
            Kleinunternehmer nach §19 UStG (Belege ohne Umsatzsteuer)
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
            Eigentumsvorbehalt
            <textarea
              rows={2}
              value={firma.eigentumsvorbehalt ?? ''}
              onChange={e => setFirma({ ...firma, eigentumsvorbehalt: e.target.value })}
              style={{ padding: '8px 10px', width: '100%', boxSizing: 'border-box' }}
            />
          </label>
          <fieldset style={{ border: '1px solid var(--bone-3)', padding: '12px 16px', margin: '0 0 16px', maxWidth: 640 }}>
            <legend style={{ padding: '0 6px', fontSize: 13, fontWeight: 600 }}>E-Rechnungs-Pflicht</legend>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              Empfangen müssen alle Unternehmen E-Rechnungen seit dem 01.01.2025. <strong>Ausstellen</strong> müssen
              Sie sie für Rechnungen an inländische Unternehmer (B2B) ab dem <strong>01.01.2027</strong>, wenn Ihr
              Vorjahresumsatz über 800.000 € lag, sonst ab dem <strong>01.01.2028</strong>. Der Stichtag steuert
              die Hinweise beim Erstellen von Rechnungen und die Warnung „Unternehmerkunden ohne E-Rechnungsformat“.
            </p>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 240 }}>
              Pflicht zur E-Rechnung ab
              <input
                type="date"
                min={PFLICHT_AB_MIN}
                max={PFLICHT_AB_MAX}
                value={firma.erechnung_pflicht_ab_gesetzt ?? ''}
                onChange={e => setFirma({ ...firma, erechnung_pflicht_ab_gesetzt: e.target.value || null })}
                style={{ padding: '7px 10px' }}
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0 4px' }}>
              <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                      onClick={() => setFirma({ ...firma, erechnung_pflicht_ab_gesetzt: PFLICHT_AB_GROSSBETRIEB })}>
                01.01.2027 (Vorjahresumsatz über 800.000 €)
              </button>
              <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                      onClick={() => setFirma({ ...firma, erechnung_pflicht_ab_gesetzt: null })}>
                Standard verwenden
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              {firma.erechnung_pflicht_ab_gesetzt
                ? `Eigener Stichtag: ${datumDe(firma.erechnung_pflicht_ab_gesetzt)}.`
                : `Leer = Standard (${datumDe(firma.erechnung_pflicht_ab_standard) || '01.01.2028'}).`}
              {' '}Erlaubt sind Stichtage von 01.01.2025 bis 31.12.2030.
            </div>
          </fieldset>
          <button type="submit" className="k-btn k-btn--primary" disabled={saving}>
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </form>
      )}

      {reiter === 'banken' && <FirmaBankverbindungen onFehler={setFehler} />}
      {reiter === 'briefpapier' && (
        <FirmaBriefpapier firma={firma} onFirmaChange={setFirma} onFehler={setFehler} />
      )}
      {reiter === 'texte' && <FirmaBelegtexte onFehler={setFehler} />}
      {reiter === 'mails' && <FirmaMailvorlagen onFehler={setFehler} />}
    </div>
  );
};

export default FirmaSection;
